import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  alignVariants,
  gapVariants,
  type LayoutAlign,
  type SpacingToken,
} from "./shared";

const stackVariants = cva("flex flex-col", {
  variants: {
    align: alignVariants,
    gap: gapVariants,
    fullWidth: {
      true: "w-full",
      false: null,
    },
  },
  defaultVariants: {
    gap: 16,
    fullWidth: false,
  },
});

export interface StackProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  /** Gap between children, maps to --spacing-{n}. Defaults to 16. */
  gap?: SpacingToken;
  /** Alignment of items on the horizontal axis */
  align?: LayoutAlign;
  /** Stretch the stack to fill the available horizontal space. */
  fullWidth?: boolean;
  children?: React.ReactNode;
}

/**
 * Vertical flex layout with tokenized gap.
 *
 * @example
 * <Stack gap={24}>
 *   <Title size="lg">Settings</Title>
 *   <Text>Configure your preferences.</Text>
 * </Stack>
 */
function Stack({
  gap,
  align,
  fullWidth = false,
  children,
  ...props
}: StackProps) {
  return (
    <div className={cn(stackVariants({ gap, align, fullWidth }))} {...props}>
      {children}
    </div>
  );
}

export { Stack };
